import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image, Plus, RefreshCw, Save, Ship, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

type CommercialUse = 'charter' | 'sale' | 'management' | 'mixed' | 'unknown';
type AssetStatus = 'draft' | 'active' | 'inactive' | 'archived';
type MediaType = 'photo' | 'video' | 'brochure' | 'layout' | 'other';

interface BrokerageAsset {
  id: string;
  name: string;
  builder: string | null;
  model: string | null;
  year_built: number | null;
  length_m: number | null;
  current_location: string | null;
  commercial_use: CommercialUse;
  asset_status: AssetStatus;
  notes: string | null;
  updated_at?: string | null;
}

interface AssetMedia {
  id: string;
  media_type: MediaType;
  url: string;
  label: string | null;
  is_hero: boolean;
  sort_order: number;
}

interface BrokerageAssetsPanelProps {
  brokerageId: string;
}

const EMPTY_ASSET_FORM = {
  name: '',
  builder: '',
  model: '',
  year_built: '',
  length_m: '',
  current_location: '',
  commercial_use: 'unknown' as CommercialUse,
  asset_status: 'draft' as AssetStatus,
  notes: '',
};

const EMPTY_MEDIA_FORM = {
  url: '',
  label: '',
  media_type: 'photo' as MediaType,
  is_hero: false,
  sort_order: '0',
};

// The generated Supabase types do not yet include the brokerage asset tables.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const toNumberOrNull = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const BrokerageAssetsPanel = ({ brokerageId }: BrokerageAssetsPanelProps) => {
  const queryClient = useQueryClient();
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET_FORM);
  const [mediaForm, setMediaForm] = useState(EMPTY_MEDIA_FORM);
  const [feedback, setFeedback] = useState<string | null>(null);

  const assetsQuery = useQuery({
    queryKey: ['brokerage-assets', brokerageId],
    queryFn: async () => {
      const { data, error } = await db
        .from('brokerage_assets')
        .select('*')
        .eq('brokerage_id', brokerageId)
        .neq('asset_status', 'archived')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []) as BrokerageAsset[];
    },
    enabled: !!brokerageId,
  });

  const selectedAsset = useMemo(
    () => assetsQuery.data?.find((asset) => asset.id === selectedAssetId) || null,
    [assetsQuery.data, selectedAssetId]
  );

  const mediaQuery = useQuery({
    queryKey: ['asset-media', brokerageId, selectedAssetId],
    queryFn: async () => {
      const { data, error } = await db
        .from('asset_media')
        .select('*')
        .eq('brokerage_id', brokerageId)
        .eq('brokerage_asset_id', selectedAssetId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []) as AssetMedia[];
    },
    enabled: !!brokerageId && !!selectedAssetId,
  });

  const createAssetMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        brokerage_id: brokerageId,
        name: assetForm.name.trim(),
        builder: assetForm.builder.trim() || null,
        model: assetForm.model.trim() || null,
        year_built: toNumberOrNull(assetForm.year_built),
        length_m: toNumberOrNull(assetForm.length_m),
        current_location: assetForm.current_location.trim() || null,
        commercial_use: assetForm.commercial_use,
        asset_status: assetForm.asset_status,
        notes: assetForm.notes.trim() || null,
      };

      if (!payload.name) {
        throw new Error('Asset name is required.');
      }

      const { data, error } = await db.from('brokerage_assets').insert(payload).select('id').single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: async (asset) => {
      setFeedback('Asset draft saved.');
      setAssetForm(EMPTY_ASSET_FORM);
      setSelectedAssetId(asset.id);
      await queryClient.invalidateQueries({ queryKey: ['brokerage-assets', brokerageId] });
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not save asset.');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ assetId, status }: { assetId: string; status: AssetStatus }) => {
      const { error } = await db
        .from('brokerage_assets')
        .update({ asset_status: status })
        .eq('brokerage_id', brokerageId)
        .eq('id', assetId);

      if (error) throw error;
    },
    onSuccess: async () => {
      setFeedback('Asset status updated.');
      await queryClient.invalidateQueries({ queryKey: ['brokerage-assets', brokerageId] });
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not update asset.');
    },
  });

  const addMediaMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAssetId) throw new Error('Select an asset first.');

      const payload = {
        brokerage_id: brokerageId,
        brokerage_asset_id: selectedAssetId,
        media_type: mediaForm.media_type,
        url: mediaForm.url.trim(),
        label: mediaForm.label.trim() || null,
        is_hero: mediaForm.is_hero,
        sort_order: toNumberOrNull(mediaForm.sort_order) || 0,
        source: 'dashboard_manual',
      };

      if (!payload.url) {
        throw new Error('Media URL is required.');
      }

      const { error } = await db.from('asset_media').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      setFeedback('Asset media linked.');
      setMediaForm(EMPTY_MEDIA_FORM);
      await queryClient.invalidateQueries({ queryKey: ['asset-media', brokerageId, selectedAssetId] });
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not link media.');
    },
  });

  return (
    <section className="mt-8 rounded-2xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Ship className="h-5 w-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">Private Assets & Media</h3>
          </div>
          <p className="mt-1 text-sm text-slate-600">Private brokerage inventory</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            assetsQuery.refetch();
            if (selectedAssetId) mediaQuery.refetch();
          }}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {feedback && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {feedback}
        </div>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Asset List</h4>
            <Badge variant="outline">{assetsQuery.data?.length || 0} assets</Badge>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="grid grid-cols-[1.4fr_1fr_0.8fr_0.8fr] bg-slate-50 px-4 py-2 text-xs font-semibold uppercase text-slate-500">
              <span>Yacht</span>
              <span>Location</span>
              <span>Use</span>
              <span>Status</span>
            </div>
            <div className="max-h-80 overflow-auto">
              {assetsQuery.isLoading && (
                <div className="px-4 py-6 text-sm text-slate-500">Loading assets...</div>
              )}
              {assetsQuery.error && (
                <div className="px-4 py-6 text-sm text-red-700">
                  Asset data unavailable.
                </div>
              )}
              {!assetsQuery.isLoading && !assetsQuery.error && assetsQuery.data?.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500">No private assets loaded yet.</div>
              )}
              {assetsQuery.data?.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => setSelectedAssetId(asset.id)}
                  className={`grid w-full grid-cols-[1.4fr_1fr_0.8fr_0.8fr] px-4 py-3 text-left text-sm transition hover:bg-emerald-50 ${
                    selectedAssetId === asset.id ? 'bg-emerald-50' : 'bg-white'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-900">{asset.name}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {[asset.builder, asset.model, asset.length_m ? `${asset.length_m}m` : null]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  </span>
                  <span className="truncate text-slate-600">{asset.current_location || 'Unknown'}</span>
                  <span className="capitalize text-slate-600">{asset.commercial_use}</span>
                  <span className="capitalize text-slate-600">{asset.asset_status}</span>
                </button>
              ))}
            </div>
          </div>

          {selectedAsset && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatusMutation.mutate({ assetId: selectedAsset.id, status: 'active' })}
              >
                Mark Active
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatusMutation.mutate({ assetId: selectedAsset.id, status: 'inactive' })}
              >
                Mark Inactive
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => updateStatusMutation.mutate({ assetId: selectedAsset.id, status: 'archived' })}
              >
                Archive
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-700" />
              <h4 className="font-semibold text-slate-900">Add Asset</h4>
            </div>
            <div className="grid gap-3">
              <div>
                <Label htmlFor="asset-name">Yacht name</Label>
                <Input
                  id="asset-name"
                  value={assetForm.name}
                  onChange={(event) => setAssetForm({ ...assetForm, name: event.target.value })}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="asset-builder">Builder</Label>
                  <Input
                    id="asset-builder"
                    value={assetForm.builder}
                    onChange={(event) => setAssetForm({ ...assetForm, builder: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="asset-model">Model</Label>
                  <Input
                    id="asset-model"
                    value={assetForm.model}
                    onChange={(event) => setAssetForm({ ...assetForm, model: event.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label htmlFor="asset-year">Year</Label>
                  <Input
                    id="asset-year"
                    inputMode="numeric"
                    value={assetForm.year_built}
                    onChange={(event) => setAssetForm({ ...assetForm, year_built: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="asset-length">Length m</Label>
                  <Input
                    id="asset-length"
                    inputMode="decimal"
                    value={assetForm.length_m}
                    onChange={(event) => setAssetForm({ ...assetForm, length_m: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="asset-location">Location</Label>
                  <Input
                    id="asset-location"
                    value={assetForm.current_location}
                    onChange={(event) => setAssetForm({ ...assetForm, current_location: event.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="asset-use">Use</Label>
                  <select
                    id="asset-use"
                    value={assetForm.commercial_use}
                    onChange={(event) =>
                      setAssetForm({ ...assetForm, commercial_use: event.target.value as CommercialUse })
                    }
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="unknown">Unknown</option>
                    <option value="charter">Charter</option>
                    <option value="sale">Sale</option>
                    <option value="management">Management</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="asset-status">Status</Label>
                  <select
                    id="asset-status"
                    value={assetForm.asset_status}
                    onChange={(event) =>
                      setAssetForm({ ...assetForm, asset_status: event.target.value as AssetStatus })
                    }
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="asset-notes">Private notes</Label>
                <Textarea
                  id="asset-notes"
                  value={assetForm.notes}
                  onChange={(event) => setAssetForm({ ...assetForm, notes: event.target.value })}
                  rows={3}
                />
              </div>
              <Button
                type="button"
                onClick={() => createAssetMutation.mutate()}
                disabled={createAssetMutation.isPending}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Save className="h-4 w-4" />
                Save Asset
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Image className="h-4 w-4 text-emerald-700" />
              <h4 className="font-semibold text-slate-900">Asset Media</h4>
            </div>
            {!selectedAsset && (
              <p className="text-sm text-slate-500">No asset selected.</p>
            )}
            {selectedAsset && (
              <div className="space-y-4">
                <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  Selected: <span className="font-medium">{selectedAsset.name}</span>
                </div>

                <div className="space-y-2">
                  {mediaQuery.isLoading && <p className="text-sm text-slate-500">Loading media...</p>}
                  {mediaQuery.data?.map((media) => (
                    <div key={media.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-800">{media.label || media.media_type}</p>
                        <a
                          href={media.url}
                          target="_blank"
                          rel="noreferrer"
                          className="block truncate text-xs text-emerald-700 hover:underline"
                        >
                          {media.url}
                        </a>
                      </div>
                      {media.is_hero && <Badge>Hero</Badge>}
                    </div>
                  ))}
                  {!mediaQuery.isLoading && mediaQuery.data?.length === 0 && (
                    <p className="text-sm text-slate-500">No media linked yet.</p>
                  )}
                </div>

                <div className="grid gap-3">
                  <div>
                    <Label htmlFor="media-url">Media URL</Label>
                    <Input
                      id="media-url"
                      value={mediaForm.url}
                      onChange={(event) => setMediaForm({ ...mediaForm, url: event.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="media-label">Label</Label>
                      <Input
                        id="media-label"
                        value={mediaForm.label}
                        onChange={(event) => setMediaForm({ ...mediaForm, label: event.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="media-type">Type</Label>
                      <select
                        id="media-type"
                        value={mediaForm.media_type}
                        onChange={(event) =>
                          setMediaForm({ ...mediaForm, media_type: event.target.value as MediaType })
                        }
                        className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="photo">Photo</option>
                        <option value="video">Video</option>
                        <option value="brochure">Brochure</option>
                        <option value="layout">Layout</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={mediaForm.is_hero}
                        onChange={(event) => setMediaForm({ ...mediaForm, is_hero: event.target.checked })}
                      />
                      Hero image
                    </label>
                    <Input
                      aria-label="Media sort order"
                      inputMode="numeric"
                      value={mediaForm.sort_order}
                      onChange={(event) => setMediaForm({ ...mediaForm, sort_order: event.target.value })}
                      className="w-24"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addMediaMutation.mutate()}
                    disabled={addMediaMutation.isPending}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Link Media
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
